import { useForm } from "react-hook-form";
import { Form } from "../../components/shadcn-ui/form";
import { Card } from "../../components/shadcn-ui/card";
import { Button } from "../../components/shadcn-ui/button";
import { Input } from "../../components/shadcn-ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/shadcn-ui/select";
import { useToast } from "@customHooks/useToast";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { addMember } from "../../features/groups/services/groupsService";
import { LEADS_FORM_FIELDS } from "features/loanApplications/formFieldsConfig";
import { DynamicFormField, FieldType } from "../../components/ui/DynamicInput";
import { CITY_MAP, INDIAN_STATES } from "features/loanApplications/formFieldsConfig/indiaStatesList";
import { createLead, checkLeadExists } from "../../features/leads/services/leadsService";
import { getContractorDetails, ContractorDetails } from "../../features/contractor/services/contractorService";
import { isFieldAsCity, decodeURIComponent } from "@lib/utils";
import { useAuth } from "context/AuthContext";
import { Loader2, AlertCircle, CheckCircle2 } from "lucide-react";

// Validation states for the two-step flow
type ValidationState =
    | 'INITIAL'           // Step 1: Phone + channel input
    | 'VALIDATING'        // Loading state
    | 'CONTRACTOR_FOUND'  // Step 2: Contractor preview shown
    | 'FORM'              // Step 3: Full form shown
    | 'ERROR';            // Error state

type FormData = {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    email: string;
    addressLineOne: string;
    addressLineTwo: string;
    city: string;
    state: string;
    postalCode: string;
    referrer: string;
    channel: string;
    pilId?: string;
};

// Sourcing channel options
const SOURCING_CHANNELS = [
    "PIL BD",
    "PIL Sales",
    "PIL Contractor",
    "Dealer",
    "Website",
    "Existing Customer",
    "WhatsApp",
    "FCC",
    "Other",
    "Call Center"
];

/**
 * Maps contractor data to form fields
 */
const mapContractorToFormData = (contractor: ContractorDetails): Partial<FormData> => {
    const names = contractor.contractor_name.trim().split(/\s+/);
    const firstName = names[0] || "";
    const lastName = names.length > 1 ? names.slice(1).join(" ") : firstName;

    // Validate state against INDIAN_STATES
    const state = INDIAN_STATES.includes(contractor.state) ? contractor.state : "";

    // Validate city against CITY_MAP for the selected state
    const citiesForState = state ? CITY_MAP[state] : [];
    const city = citiesForState?.includes(contractor.city) ? contractor.city : "";

    return {
        firstName,
        lastName,
        postalCode: contractor.pincode || "",
        state,
        city,
    };
};

/**
 * Formats the membership date for display
 */
const formatMemberSince = (dateString: string): string => {
    try {
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    } catch {
        return dateString;
    }
};

export function AddMemberPage() {
    const navigate = useNavigate();
    const { toast } = useToast();
    const { userDetails } = useAuth();
    const { id: groupId } = useParams();
    const [searchParams] = useSearchParams();
    const groupCity = searchParams.get("city");

    // State management
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validationState, setValidationState] = useState<ValidationState>('INITIAL');
    const [contractorData, setContractorData] = useState<ContractorDetails | null>(null);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [leadExists, setLeadExists] = useState(false);
    const [existingLead, setExistingLead] = useState<any>(null);

    const form = useForm<FormData>({
        mode: "all",
        defaultValues: LEADS_FORM_FIELDS.reduce((acc: any, field: FieldType) => {
            acc[field.name] =
                field.name === "referrer" ? userDetails?.email || "" : "";
            return acc;
        }, {} as FormData),
    });

    // Handle contractor validation (Step 1 -> Step 2 or Error)
    const onValidate = async () => {
        const phoneNumber = form.getValues("phoneNumber");
        const channel = form.getValues("channel");

        if (!phoneNumber || phoneNumber.length !== 10 || !channel) {
            return;
        }

        setValidationState('VALIDATING');
        setErrorMessage('');

        try {
            // Check contractor API
            const contractor = await getContractorDetails(phoneNumber);

            if (!contractor) {
                setValidationState('ERROR');
                setErrorMessage(
                    "The phone number does not belong to a registered contractor. Please enter a valid contractor phone number to proceed."
                );
                return;
            }

            setContractorData(contractor);
            setValidationState('CONTRACTOR_FOUND');

        } catch (error) {
            console.error("Error validating contractor:", error);
            setValidationState('ERROR');
            setErrorMessage("An error occurred while validating the contractor. Please try again.");
        }
    };

    // Handle proceed from contractor preview (Step 2 -> Step 3)
    const onProceed = async () => {
        const phoneNumber = form.getValues("phoneNumber");

        try {
            // Check if lead exists in contacts database
            const lead = await checkLeadExists(phoneNumber) as any;

            if (lead) {
                // Check if lead is already a member of a group
                if (lead.metaData?.memberOf) {
                    setValidationState('ERROR');
                    setErrorMessage(
                        `This person is already a member of group "${lead.metaData.memberOf.name}". Please use a different phone number.`
                    );
                    toast({
                        variant: "destructive",
                        title: "Lead already in a group",
                        description: `${lead.firstName} is already a member of "${lead.metaData.memberOf.name}"`,
                    });
                    return;
                }

                // Lead exists - populate from lead data
                setExistingLead(lead);
                setLeadExists(true);
                form.setValue("firstName", lead.firstName || "");
                form.setValue("lastName", lead.lastName || "");
                form.setValue("email", lead.email || "");

                if (lead.address) {
                    form.setValue("addressLineOne", lead.address.addressLineOne || "");
                    form.setValue("addressLineTwo", lead.address.addressLineTwo || "");
                    form.setValue("postalCode", lead.address.postalCode || "");
                    form.setValue("state", lead.address.state || "");

                    const cityValue = lead.address.city;
                    if (cityValue) {
                        setTimeout(() => {
                            form.setValue("city", cityValue);
                        }, 0);
                    }
                }

                if (lead.source) {
                    form.setValue("channel", lead.source.channel || "");
                }

                toast({
                    description: `Existing lead found for ${phoneNumber}!`,
                });
            } else if (contractorData) {
                // Lead doesn't exist - populate from contractor data
                const mappedData = mapContractorToFormData(contractorData);
                form.setValue("firstName", mappedData.firstName || "");
                form.setValue("lastName", mappedData.lastName || "");
                form.setValue("state", mappedData.state || "");
                if (mappedData.city) {
                    setTimeout(() => {
                        form.setValue("city", mappedData.city || "");
                    }, 0);
                }
                form.setValue("postalCode", mappedData.postalCode || "");
                setLeadExists(false);
                setExistingLead(null);
            }

            setValidationState('FORM');

        } catch (error) {
            console.error("Error checking lead:", error);
            // Even if lead check fails, proceed with contractor data
            if (contractorData) {
                const mappedData = mapContractorToFormData(contractorData);
                form.setValue("firstName", mappedData.firstName || "");
                form.setValue("lastName", mappedData.lastName || "");
                form.setValue("state", mappedData.state || "");
                if (mappedData.city) {
                    setTimeout(() => {
                        form.setValue("city", mappedData.city || "");
                    }, 0);
                }
                form.setValue("postalCode", mappedData.postalCode || "");
            }
            setValidationState('FORM');
        }
    };

    // Reset back to initial state
    const onReset = () => {
        const channel = form.getValues("channel");
        form.reset({
            ...LEADS_FORM_FIELDS.reduce((acc: any, field: FieldType) => {
                acc[field.name] = field.name === "referrer" ? userDetails?.email || "" : "";
                return acc;
            }, {} as FormData),
            channel, // Keep the selected channel
        });
        setValidationState('INITIAL');
        setContractorData(null);
        setErrorMessage('');
        setLeadExists(false);
        setExistingLead(null);
    };

    const handleStateChange = (name: string, value: any) => {
        form.setValue(name as any, value);
        form.setValue("city", "");
    };

    const onSubmit = async (formState: FormData) => {
        if (!groupId || !groupCity) {
            toast({
                variant: "destructive",
                description: "Group information missing",
            });
            return;
        }

        setIsSubmitting(true);
        try {
            let contactId = existingLead?.id;
            const phoneNumber = formState.phoneNumber;

            // Create lead if doesn't exist
            if (!leadExists) {
                const leadData = {
                    firstName: formState.firstName,
                    lastName: formState.lastName,
                    phoneNumber: formState.phoneNumber,
                    email: formState.email,
                    address: {
                        addressLineOne: formState.addressLineOne,
                        addressLineTwo: formState.addressLineTwo,
                        city: formState.city,
                        state: formState.state,
                        postalCode: formState.postalCode,
                    },
                    referral: formState.referrer,
                    channel: formState.channel,
                    pilId: formState.pilId,
                };

                const createdLead = await createLead(leadData) as any;
                contactId = createdLead.id;
                toast({
                    description: "Lead created successfully!",
                });
            }

            // Add member to group
            await addMember({
                id: decodeURIComponent(groupId),
                city: decodeURIComponent(groupCity),
                member: {
                    contactId,
                    lastName: formState.lastName,
                    phoneNumber,
                },
                role: "MEMBER"
            });

            toast({
                title: "Member added successfully!",
                description: `${formState.firstName} ${formState.lastName} has been added to the group`,
            });
            navigate(`/dashboard/groups/${groupId}?city=${groupCity}`);
        } catch (err: any) {
            console.error("Error adding member:", err);
            toast({
                variant: "destructive",
                description: err?.message || "Failed to add member.",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    // Render Step 1: Phone + Channel Validation
    const renderValidationStep = () => {
        const phoneNumber = form.watch("phoneNumber");
        const channel = form.watch("channel");
        const isValidPhone = phoneNumber?.length === 10 && /^\d{10}$/.test(phoneNumber);
        const canValidate = isValidPhone && channel && validationState !== 'VALIDATING';

        return (
            <>
                <p className="text-sm text-muted-foreground mb-4">
                    Enter phone number and sourcing channel to validate the contractor.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 max-w-2xl">
                    <div>
                        <label className="text-sm font-medium">Phone Number *</label>
                        <Input
                            placeholder="10-digit phone number"
                            maxLength={10}
                            {...form.register("phoneNumber", {
                                required: "Phone number is required",
                                pattern: { value: /^\d{10}$/, message: "Must be 10 digits" },
                            })}
                            disabled={validationState === 'VALIDATING'}
                        />
                        {form.formState.errors.phoneNumber && (
                            <p className="text-red-500 text-xs mt-1">
                                {form.formState.errors.phoneNumber.message}
                            </p>
                        )}
                    </div>

                    <div>
                        <label className="text-sm font-medium">Sourcing Channel *</label>
                        <Select
                            onValueChange={(value) => form.setValue("channel", value)}
                            value={channel}
                            disabled={validationState === 'VALIDATING'}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Select a channel" />
                            </SelectTrigger>
                            <SelectContent>
                                {SOURCING_CHANNELS.map((ch) => (
                                    <SelectItem key={ch} value={ch}>
                                        {ch}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {form.formState.errors.channel && (
                            <p className="text-red-500 text-xs mt-1">
                                {form.formState.errors.channel.message}
                            </p>
                        )}
                    </div>
                </div>

                {/* Error Message */}
                {validationState === 'ERROR' && errorMessage && (
                    <div className="flex items-start gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-md max-w-2xl">
                        <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-700">{errorMessage}</p>
                    </div>
                )}

                <div className="flex gap-2">
                    <Button
                        type="button"
                        onClick={onValidate}
                        disabled={!canValidate}
                    >
                        {validationState === 'VALIDATING' ? (
                            <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Validating...
                            </>
                        ) : (
                            "Validate Contractor"
                        )}
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => navigate(`/dashboard/groups/${groupId}?city=${groupCity}`)}
                    >
                        Cancel
                    </Button>
                </div>
            </>
        );
    };

    // Render Step 2: Contractor Preview
    const renderContractorPreview = () => {
        if (!contractorData) return null;

        return (
            <>
                <div className="flex items-center gap-2 mb-4">
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" />
                        Verified Contractor
                    </span>
                </div>

                {/* Contractor Details Preview Card */}
                <div className="bg-gray-50 border rounded-lg p-4 mb-4 max-w-2xl">
                    <h5 className="font-medium text-gray-700 mb-3">Contractor Details</h5>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                        <div>
                            <span className="text-gray-500">Name:</span>
                            <p className="font-medium">{contractorData.contractor_name}</p>
                        </div>
                        <div>
                            <span className="text-gray-500">Phone:</span>
                            <p className="font-medium">{contractorData.contractor_mobile_number}</p>
                        </div>
                        <div>
                            <span className="text-gray-500">Membership #:</span>
                            <p className="font-medium">{contractorData.membership_number}</p>
                        </div>
                        <div>
                            <span className="text-gray-500">Member Since:</span>
                            <p className="font-medium">{formatMemberSince(contractorData.member_since)}</p>
                        </div>
                        <div>
                            <span className="text-gray-500">City:</span>
                            <p className="font-medium">{contractorData.city || "-"}</p>
                        </div>
                        <div>
                            <span className="text-gray-500">State:</span>
                            <p className="font-medium">{contractorData.state || "-"}</p>
                        </div>
                        <div>
                            <span className="text-gray-500">Pincode:</span>
                            <p className="font-medium">{contractorData.pincode || "-"}</p>
                        </div>
                        <div>
                            <span className="text-gray-500">Classification:</span>
                            <p className="font-medium">{contractorData.contractor_classification || "-"}</p>
                        </div>
                        <div>
                            <span className="text-gray-500">Club:</span>
                            <p className="font-medium">{contractorData.clubname || "-"}</p>
                        </div>
                    </div>
                </div>

                <div className="flex gap-2">
                    <Button
                        type="button"
                        onClick={onProceed}
                        className="btn-primary"
                    >
                        Proceed to Form
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={onReset}
                    >
                        Change Phone Number
                    </Button>
                </div>
            </>
        );
    };

    // Render Step 3: Full Form
    const renderForm = () => {
        const selectedState = form.watch("state");

        return (
            <>
                <div className="flex items-center gap-2 mb-4">
                    {leadExists ? (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                            ✓ Existing Lead
                        </span>
                    ) : contractorData ? (
                        <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                            New Lead (from Contractor)
                        </span>
                    ) : null}
                </div>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>
                        <div className="flex flex-wrap gap-y-4 gap-x-8 w-full">
                            {LEADS_FORM_FIELDS.map((field: FieldType, index: number) => {
                                // Check if the field has a value in the existing lead data
                                const hasExistingValue = () => {
                                    if (!existingLead) return false;
                                    if (field.name === "phoneNumber") return false;

                                    if (field.name === "addressLineOne" || field.name === "addressLineTwo" || field.name === "city" || field.name === "state" || field.name === "postalCode") {
                                        return !!existingLead.address?.[field.name];
                                    }
                                    return !!existingLead[field.name];
                                };

                                // Phone is always disabled (already validated)
                                // For existing leads, disable fields with values
                                // For new leads from contractor, all fields are editable except phone
                                const isFieldDisabled =
                                    field.name === "phoneNumber" ||
                                    (leadExists && hasExistingValue()) ||
                                    field.name === "referrer";

                                const dynamicOptions =
                                    isFieldAsCity(field.name)
                                        ? CITY_MAP[selectedState] || []
                                        : field.options || [];

                                const handleChange =
                                    field.name === "state" && !existingLead
                                        ? handleStateChange
                                        : undefined;

                                return (
                                    <div key={index} className="w-full">
                                        <DynamicFormField
                                            field={{ ...field, options: dynamicOptions } as FieldType}
                                            isDisabled={isFieldDisabled}
                                            handleChange={handleChange}
                                            {...form.register(field.name as keyof FormData)}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                        <div className="flex gap-2 mt-6">
                            <Button type="submit" className="btn-primary" disabled={isSubmitting}>
                                {isSubmitting ? "Adding Member..." : "Add Member"}
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => navigate(`/dashboard/groups/${groupId}?city=${groupCity}`)}
                            >
                                Cancel
                            </Button>
                        </div>
                    </form>
                </Form>
            </>
        );
    };

    return (
        <>
            <strong className="block mt-0 mb-2 text-xl bg-blue-200 p-2 pl-4 rounded-sm">
                Add Member to Group
            </strong>
            <Card className="p-4 h-full md:pt-6">
                <div className="flex flex-col md:pl-4 flex-wrap">
                    <div className="w-full">
                        {(validationState === 'INITIAL' || validationState === 'VALIDATING' || validationState === 'ERROR') && renderValidationStep()}
                        {validationState === 'CONTRACTOR_FOUND' && renderContractorPreview()}
                        {validationState === 'FORM' && renderForm()}
                    </div>
                </div>
            </Card>
        </>
    );
}
